import React from 'react';
import { Typography, Box } from '@mui/material';
import { Translate } from 'react-localize-redux';
import { DiagnosisTableData } from '../types';
import DiagnosisRowsTable from './DiagnosisRowsTable';

interface DiagnosisTableProps {
  data: DiagnosisTableData;
  title: string;
}

/**
 * SHER-306 / SHER-583. The outpatient ICD-11 panel: "Patients by recorded diagnosis (ICD-11)".
 *
 * The table itself is `DiagnosisRowsTable`, shared with the inpatient discharge panel, because
 * the client asked on 2026-09-18 for the two to stop looking like different tables. What stays
 * here is what genuinely differs: this panel's title, its subtitle, and its empty state.
 *
 * No `useState`/`useEffect` any more. The rows arrive assembled from `transformUniquePatientData`
 * rather than as index-aligned arrays this component had to re-zip on every change of either
 * `data` or the patient store.
 */
const DiagnosisTable: React.FC<DiagnosisTableProps> = ({ data, title }) => {
  const rows = data?.rows ?? [];

  if (rows.length === 0) {
    return (
      <Box p={3}>
        <Typography variant="subtitle1">No diagnosis data available</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{title}</Typography>
      {/* SHER-306: the client asked for this line on 2026-09-18. It states the population the
          table reports, which is what stops the figures being read as consultations. */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        <Translate id="hospital.analytics.outpatient_diagnoses.subtitle" />
      </Typography>
      <DiagnosisRowsTable rows={rows} />
    </Box>
  );
};

export default DiagnosisTable;
